import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Notification } from '@bringg/react-components';
import useStore from '../../stores/helpers/use-context';
import useClickOutside from '../../hooks/use-click-outside';
import TaskTooltipBody from './task-tooltip-body';
import { useSkillsValidator } from '../../components/routes-planner/hooks/use-skills-validator';
import MissingSkillsModal from '../../components/missing-skills-modal/missing-skills-modal';
import { getResourceDeliveryBlocks } from '../routes-planner/timeline-logic/delivery-block-logic';
import type { RoutesPlannerTask } from '@bringg-frontend/routes-planner';

type Props = {
	taskIds: number[];
	containerRef?: React.MutableRefObject<HTMLDivElement>;
	removeTooltip?: () => void;
	setShouldShowTooltipArrow?: (ShouldShowTooltipArrow: boolean) => void;
};

const TaskTooltipWrapper: React.FC<Props> = ({ taskIds, removeTooltip, containerRef, setShouldShowTooltipArrow }) => {
	const routesPlannerStore = useStore();

	const { notificationsTranslations } = routesPlannerStore.translations;
	const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);
	const [missingSkillsModalVisible, setMissingSkillsModalVisible] = useState(false);
	const [taskTooltipVisible, setTaskTooltipVisible] = useState(true);
	const [missingSkills, setMissingSkills] = useState([]);
	const [updatedTaskData, setUpdatedTaskData] = useState({ priority: null, routeId: null });

	useEffect(() => {
		if (routesPlannerStore.inProgressEtaCalculationsList.size) {
			removeTooltip?.();
		}
	}, [routesPlannerStore.inProgressEtaCalculationsList, removeTooltip]);

	useEffect(() => {
		routesPlannerStore.setSelectedTaskId(taskIds[currentItemIndex]);
	}, [currentItemIndex, routesPlannerStore.setSelectedTaskId, taskIds]);

	useEffect(() => {
		return () => {
			setShouldShowTooltipArrow?.(true);
		};
	}, [setShouldShowTooltipArrow]);

	useClickOutside(containerRef, taskTooltipVisible, removeTooltip);

	const { areAllRequiredSkillsOnNewRoute } = useSkillsValidator();

	const toolTipTasks = taskIds.map(taskId => routesPlannerStore.tasksById.get(taskId)).filter(Boolean);

	const dataToDisplay =
		toolTipTasks.length !== taskIds.length
			? ({} as RoutesPlannerTask)
			: toolTipTasks[currentItemIndex] ?? toolTipTasks[0];

	const resequenceTask = (priority: number, routeId: number) => {
		routesPlannerStore.resequenceTasks(routeId, [dataToDisplay?.id], priority).then(() => {
			if (isAnyTaskOutOfDeliveryBlockRange(routeId)) {
				if (routesPlannerStore.configuration.timelineConfig.canSetTasksPastDeliveryBlock) {
					Notification.warn(
						routesPlannerStore.translations.notificationsTranslations.outOfDeliveryBlockRangeNotice
					);
				} else {
					Notification.error(
						routesPlannerStore.translations.notificationsTranslations.outOfDeliveryBlockRange
					);
					routesPlannerStore.undoLastChange();
				}
			}
		});

		removeTooltip?.();
	};

	const isAnyTaskOutOfDeliveryBlockRange = (routeId: number): boolean => {
		if (
			!getResourceDeliveryBlocks(
				routesPlannerStore.routesById[routeId],
				routesPlannerStore.deliveryBlocksByDriver,
				routesPlannerStore.deliveryBlocksByVehicle
			).length
		) {
			return false;
		}

		const tasks = routesPlannerStore.runToTasksInPresentedDate[routeId];

		return tasks.some(
			task =>
				!getResourceDeliveryBlocks(
					routesPlannerStore.routesById[routeId],
					routesPlannerStore.deliveryBlocksByDriver,
					routesPlannerStore.deliveryBlocksByVehicle,
					task.way_points[0].eta,
					task.way_points[0].etl,
					false
				).length
		);
	};

	const resequenceChange = (priority: number, routeId: number) => {
		const isRouteChanged = routeId !== dataToDisplay.run_id;
		if (isRouteChanged) {
			const { success, missingSkills } = areAllRequiredSkillsOnNewRoute([dataToDisplay], routeId);
			if (!success) {
				setMissingSkillsModalVisible(true);
				setTaskTooltipVisible(false);
				setShouldShowTooltipArrow?.(false);
				setUpdatedTaskData({ priority, routeId });
				setMissingSkills(missingSkills);
				return;
			}
		}
		resequenceTask(priority, routeId);
	};

	const onCancelModal = () => {
		removeTooltip?.();
		setMissingSkillsModalVisible(false);
	};

	const onApproveRouteNotMatchingSkills = () => {
		resequenceTask(updatedTaskData.priority, updatedTaskData.routeId);
		onCancelModal();
	};

	return (
		<div data-test-id="taskTooltipWrapper">
			{taskTooltipVisible && (
				<TaskTooltipBody
					taskIds={taskIds}
					removeTooltip={removeTooltip}
					resequenceChange={resequenceChange}
					currentItemIndex={currentItemIndex}
					setCurrentItemIndex={setCurrentItemIndex}
				/>
			)}
			<MissingSkillsModal
				isVisible={missingSkillsModalVisible}
				onOk={onApproveRouteNotMatchingSkills}
				onCancel={onCancelModal}
				translations={notificationsTranslations}
				missingSkills={missingSkills}
			/>
		</div>
	);
};

export default observer(TaskTooltipWrapper);
